/**
 * Cashflow Warnings & Upcoming Payments Section
 *
 * Shows upcoming payments (next 14 days), compact incomplete PO banner,
 * missing date alerts, and other warning alerts.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, FormControl, IconButton, InputLabel, Link, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import moment from "moment";

import { CashflowAllocation, CashflowWarning } from "models/CashflowModel";
import { PurchaseOrder } from "models/InventoryModel";

const UPCOMING_ROWS_PER_PAGE = 6;

const TIMEFRAME_OPTIONS = [
  { value: 14, label: '2 Weeks' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 180, label: '6 Months' },
] as const;

interface CashflowWarningsSectionProps {
  warnings: CashflowWarning[];
  consolidatedPOs: PurchaseOrder[];
  futureAllocations: CashflowAllocation[];
  onOpenIncompletePOs: () => void;
  onOpenWarningPO: (poNumber: string) => void;
}

export default function CashflowWarningsSection({
  warnings,
  consolidatedPOs,
  futureAllocations,
  onOpenIncompletePOs,
  onOpenWarningPO,
}: CashflowWarningsSectionProps): JSX.Element | null {
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [timeframeDays, setTimeframeDays] = useState(14);

  const upcomingPayments = useMemo(() => {
    const cutoff = moment().add(timeframeDays, 'days').format('YYYY-MM-DD');
    return futureAllocations.filter(e => e.paymentDate <= cutoff);
  }, [futureAllocations, timeframeDays]);

  const incompletePOCount = useMemo(() => {
    return warnings.filter(w => w.type === 'incomplete_po').length;
  }, [warnings]);

  const missingDates = warnings.filter(w => w.type === 'missing_date');
  const otherWarnings = warnings.filter(w => w.type !== 'incomplete_po' && w.type !== 'missing_date');

  // Reset pagination when data changes
  useEffect(() => {
    setUpcomingPage(0);
  }, [upcomingPayments]);

  const hasContent = futureAllocations.length > 0 || incompletePOCount > 0 || missingDates.length > 0 || otherWarnings.length > 0;
  if (!hasContent) return null;

  const totalUpcomingPages = Math.ceil(upcomingPayments.length / UPCOMING_ROWS_PER_PAGE);
  const safeUpcomingPage = Math.min(upcomingPage, Math.max(0, totalUpcomingPages - 1));

  return (
    <Box sx={{ mb: 2 }}>
      {/* Upcoming Payments table */}
      {futureAllocations.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, px: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              {upcomingPayments.length} upcoming payment{upcomingPayments.length !== 1 ? 's' : ''} due in the next
            </Typography>
            <FormControl size="small" sx={{ ml: 1, minWidth: 110 }}>
              <Select
                value={timeframeDays}
                onChange={(e) => {
                  setTimeframeDays(Number(e.target.value));
                  setUpcomingPage(0);
                }}
                sx={{ height: 28, fontSize: '0.8rem' }}
              >
                {TIMEFRAME_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PO #</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell>Payment Type</TableCell>
                  <TableCell align="center">Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const pageRows = upcomingPayments
                    .slice(safeUpcomingPage * UPCOMING_ROWS_PER_PAGE, (safeUpcomingPage + 1) * UPCOMING_ROWS_PER_PAGE);
                  const emptyRowCount = UPCOMING_ROWS_PER_PAGE - pageRows.length;
                  return (
                    <>
                      {pageRows.map((entry, idx) => (
                        <TableRow key={`${entry.poNumber}-${entry.paymentDate}-${idx}`} hover>
                          <TableCell>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => onOpenWarningPO(entry.poNumber)}
                            >
                              {entry.poNumber}
                            </Link>
                          </TableCell>
                          <TableCell>{entry.supplierName}</TableCell>
                          <TableCell align="right">
                            ${entry.trancheAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {moment(entry.paymentDate).format('MM/DD/YY')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={entry.paymentType}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {entry.paid ? (
                              <PaidOutlinedIcon sx={{ fontSize: 18, color: 'success.main' }} />
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {emptyRowCount > 0 && (
                        <TableRow sx={{ height: 37 * emptyRowCount }}>
                          <TableCell colSpan={6} sx={{ border: 0 }} />
                        </TableRow>
                      )}
                    </>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
          {upcomingPayments.length > UPCOMING_ROWS_PER_PAGE && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5, gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {safeUpcomingPage * UPCOMING_ROWS_PER_PAGE + 1}–{Math.min((safeUpcomingPage + 1) * UPCOMING_ROWS_PER_PAGE, upcomingPayments.length)} of {upcomingPayments.length}
              </Typography>
              <IconButton
                size="small"
                disabled={safeUpcomingPage === 0}
                onClick={() => setUpcomingPage(p => p - 1)}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={(safeUpcomingPage + 1) * UPCOMING_ROWS_PER_PAGE >= upcomingPayments.length}
                onClick={() => setUpcomingPage(p => p + 1)}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* Compact incomplete PO banner */}
      {incompletePOCount > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Button size="small" onClick={onOpenIncompletePOs}>
              Review
            </Button>
          }
        >
          {incompletePOCount} PO{incompletePOCount !== 1 ? 's' : ''} excluded from cashflow — missing required fields
        </Alert>
      )}

      {/* Missing date warnings */}
      {missingDates.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {missingDates.length} tranche{missingDates.length !== 1 ? 's' : ''} skipped due to missing dates:
          <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
            {missingDates.map((w, idx) => (
              <li key={idx}>{w.message}</li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Other warnings */}
      {otherWarnings.map((w, idx) => (
        <Alert key={idx} severity="warning" sx={{ mb: 1 }}>
          {w.message}
        </Alert>
      ))}
    </Box>
  );
}
